import { getMeasureBeatCount } from './measure_beat_counts.ts';
import { MessageType, SongFileExtension, Voice, voicesRequiringClef } from './model.ts';
import { ensureExists, ensureUnreachable, NonEmptyArray } from './util.ts';

// Goals of new format:
// - Represent any Music XML input file
//   - Chords (coinciding notes -> Note[])
//   - Stem up / down
//   - Arbitrary number of voices (phrases) per measure
//   - Slurs / ties between chords (slurring/tying all notes)
//   - Note articulations (staccato / fermata)
// - *** Import should be a series of transformations across measures ***
//   - Can debug at any step
//   - Can output at any non-failing step
//   - Output includes warnings / errors
// - Begin to distinguish between tune & lyrics
// - MIDI should mark tracks with instrument name
//   - MIDI generator should fill in rests for measures that omit a particular voice
// - MIDI should mark the beginning of each measure with an appropriate event - ideally a measure number
//   - Do any existing music programs output measure markers - if so, how?
// - Fermatas should be applied to MIDI & should align across different note durations (e.g. "Nothing but the Blood")

// Implementation strategy:
// - Define new file format parallel to old
//   - Rename old file format to 'TxtMusicFile', with the new format being 'SypMusicFile'
// - Build new importer alongside old
// - For test execution, convert from new format to old
// - Run importer tests on new and old formats (shim that chooses each format)
// - Persist new format alongside old (.txt & .syp)
// - Rebuild Python midi generator in TypeScript on new format
// - Gradually convert app to use new format
// - Phase out old format once no longer used

// Open questions:
// * How will musical notes look in JSON?
//   * Can we retain the compact format? (how to serialize/deserialize?)
//   * Do we need to wrap each note in quotes?
// * How will repeats, codas, groups of verses, etc. be represented in the new format?
//   * Consider St Patrick's Breastplate & some more modern hymns from Hymns of Grace
//   * Consider the "flow control" degrees of freedom that can be represented with musical notation

// TODO(hewitt): Use server calculated beat counts when we have processed all hymnal & custom songs
//               with the new importer to insert beat counts
const useServerCalculatedBeatCount = false;

export enum SypMode {
  // TODO: Recognize at least Dorian mode.
  Major = 'major',
  Minor = 'minor',
}

export const defaultRhythm: SypRhythm = '0 : 4';
export const defaultSongKey: SypMeasureKey = 'C';
export const defaultMode = SypMode.Major;

export const StemUpChar = '\u2191';
export const StemDownChar = '\u2193';
export const StemDoubleChar = '\u2195';

export const sypFileExtension = '.' + SongFileExtension.Syp; // parts split
export const xmlSypFileExtension = '.xml' + sypFileExtension; // direct conversion from MusicXML to SYP

export const incorrectBeatCountError = 'Incorrect beat count in measure';

export function getDefaultTempo(rhythm?: SypRhythm) {
  // songs with half-note time signatures default to twice those with quarter notes
  return rhythm?.endsWith(' 2') ? 200 : 100;
}

export enum SypMusicFileKey {
  // Note that SypMusicFileKey includes Voice enum as well, but TypeScript does not support enum inheritance

  Title = 'title',
  Composer = 'composer',
  Author = 'author',
  Harmony = 'harmony',
  Range = 'range',
  Tune = 'tune',
  Voice = 'voice',
  Script = 'script',
  Copyright = 'copyright',
  VerseCount = 'verse_count',
  VocalPartCount = 'vocal_part_count',
  Vocals = 'vocals',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
  // TODO(hewitt): might be better modeled at the staff level, but need to get rid of old 'unison' staves first...
  Unison = 'unison', // indicates song should be unison

  // measure specific keys
  Measure = 'measure',
  Key = 'key',
  Mode = 'mode',
  Rhythm = 'rhythm',
  Tempo = 'tempo',
  Goto = 'goto',
  End = 'end',
  Iterations = 'iterations',
  SetIteration = 'set_iteration',
  BeginRepeat = 'begin_repeat',
  EndRepeat = 'end_repeat',
  BeatCount = 'beat_count',
}

// TODO(hewitt): Deprecated - combine with SypMusicFileNew
export interface SypMusicFile {
  isUnison?: boolean;
  metadata: SypSongMetadata;
  measures: SypMeasure[];
}

export interface SypMusicFileNew extends SypMusicFile {
  measures: SypMeasureNew[];
  messages: SypFileMessage[];
}

export type SypSongMetadata = { [K in SypMusicFileKey | Voice]?: string } & { [SypMusicFileKey.Tempo]: string } & {
  [SypMusicFileKey.Rhythm]: string;
} & { [SypMusicFileKey.Key]: string } & { [SypMusicFileKey.Mode]: string } & { [SypMusicFileKey.Title]: string };
export type SypMeasureKey = string; // 'C', 'Bb', 'F'
export type SypRhythm = string; // '0 : 4', '4 : 4', '6 : 8'
export interface SypTimeSignature {
  beats: number;
  beatType: number;
}

export enum SypClef {
  G = 'g',
  F = 'f',
}

export enum SypStem {
  Up = 'Up',
  Down = 'Down',
  Double = 'Double',
}

export const sypClefName: { [name: string]: SypClef } = {
  ['G']: SypClef.G,
  ['F']: SypClef.F,
};

export type MeasureNumber = number;

export interface SypMeasure {
  number: MeasureNumber; // Can be a decimal (e.g. 16.1) when an internal measure could not be joined
  key: SypMeasureKey;
  mode: SypMode;
  rhythm: SypRhythm;
  tempo: number;
  verseCount?: number;
  skipValidation?: boolean;
  barline?: SypBarlineType;

  // TODO(hewitt): Deprecated - remove these properties when there is no chance we will encounter an old file format
  soprano: SypNote[];
  alto?: SypNote[];
  tenor?: SypNote[];
  bass?: SypNote[];
}

// TODO(hewitt): combine into a single SypMeasure class
export interface SypMeasureNew extends SypMeasure {
  staves: SypStaff[];
  goto?: GotoDirective;
  end?: EndDirective;
  iterations?: number[];
  setIteration?: number; // deprecated: treated as beginRepeat (iteration argument ignored)
  beginRepeat?: boolean; // pushes a new scope onto the iteration stack (iteration starts at 1)
  endRepeat?: boolean; // pops the iteration stack when no goto fires for this scope
  beatCount: number;
}

export function getAllVoices(measure: SypMeasureNew): Voice[] {
  return measure.staves.map(({ voice }) => voice).filter((voice) => voice) as Voice[];
}

export function shouldIgnoreUnusualBeatCount(measure: SypMeasureNew, allMeasures: SypMeasureNew[], rhythm: SypRhythm) {
  if (isFirstMeasure(measure, allMeasures) || isLastMeasureForBeatCounting(measure, allMeasures)) {
    return true;
  }
  if (containsFermatas(measure)) {
    return true;
  }
  // any number of beats is allowed with this time signature
  if (getTimeSignatureFromRhythm(rhythm).beats === 0) {
    return true;
  }
  // the measure with a double barline may be a begin repeat, causing the previous measure to be shortened (cc633 m4)
  if (
    measure.barline === SypBarlineType.Double ||
    getPreviousMeasure(measure, allMeasures)?.barline === SypBarlineType.Double ||
    getNextMeasure(measure, allMeasures)?.barline === SypBarlineType.Double
  ) {
    return true;
  }
  if (
    measure.key !== getPreviousMeasure(measure, allMeasures)?.key ||
    measure.key !== getNextMeasure(measure, allMeasures)?.key
  ) {
    return true;
  }
  return false;
}

function isFirstMeasure(measure: SypMeasureNew, allMeasures: SypMeasureNew[]) {
  return measure.number === allMeasures[0].number;
}

function isLastMeasureForBeatCounting(measure: SypMeasureNew, allMeasures: SypMeasureNew[]) {
  if (measure.number === allMeasures[allMeasures.length - 1].number) {
    return true;
  }
  // When an empty measure is appended for an implicit goto, treat the prior measure as the last
  const lastMeasure = allMeasures[allMeasures.length - 1];
  if (lastMeasure.staves.every(({ notes }) => notes.length === 0)) {
    return allMeasures.length >= 2 && measure.number === allMeasures[allMeasures.length - 2].number;
  }
  return false;
}

function containsFermatas(measure: SypMeasureNew) {
  return measure.staves.some(({ notes }) =>
    notes.some((noteOrChord) =>
      isSypChord(noteOrChord) ? noteOrChord.notes.some(({ fermata }) => fermata) : noteOrChord.fermata,
    ),
  );
}

function getPreviousMeasure(measure: SypMeasureNew, allMeasures: SypMeasureNew[]) {
  const measureIndex = allMeasures.findIndex(({ number }) => number === measure.number);
  if (measureIndex === -1) {
    return undefined;
  }
  return allMeasures[measureIndex - 1];
}

function getNextMeasure(measure: SypMeasureNew, allMeasures: SypMeasureNew[]) {
  const measureIndex = allMeasures.findIndex(({ number }) => number === measure.number);
  if (measureIndex === -1) {
    return undefined;
  }
  return allMeasures[measureIndex + 1];
}

export function visitNotes(
  measures: SypMeasureNew[],
  staffIndex: StaffIndex,
  callback: (maybeChord: SypNote | SypChord) => void,
): void {
  visitStaves(measures, staffIndex, (staff) => {
    for (const note of staff.notes) {
      callback(note);
    }
  });
}

export function visitStaves(
  measures: SypMeasureNew[],
  staffIndex: StaffIndex,
  callback: (staff: SypStaff) => void,
): void {
  for (const measure of measures) {
    callback(measure.staves[staffIndex]);
  }
}

export interface GotoDirective {
  toMeasure: MeasureNumber;
  forIterations?: NonEmptyArray<number>; // missing property indicates all iterations
}

export interface EndDirective {
  forIterations?: NonEmptyArray<number>; // missing property indicates all iterations
}

const maxNonUnisonMeasurePercentage = 0.15; // more than this percentage of non-unison measures will make it not unison

export function isUnisonStaff(measures: SypMeasureNew[], staffIndex: number): boolean {
  if (measures.length === 0) {
    return true;
  }
  let nonUnisonMeasureCount = 0;
  for (const { staves } of measures) {
    if (staves[staffIndex].notes.some((note) => isSypChord(note) || note.stem === SypStem.Double)) {
      nonUnisonMeasureCount += 1;
    }
  }
  const percentageOfNonUnisonMeasures = nonUnisonMeasureCount / measures.length;
  return percentageOfNonUnisonMeasures <= maxNonUnisonMeasurePercentage;
}

export function staffHasLyrics(measures: SypMeasureNew[], staffIndex: number): boolean {
  return measures.some((measure) =>
    measure.staves[staffIndex].notes.some((note) => {
      const result = note.hasLyrics;
      return result;
    }),
  );
}

export function getMeasureNumberForDisplay(measureNumber: number): string {
  const primeCount = Math.round((measureNumber - Math.round(measureNumber)) * 10);
  return `${Math.round(measureNumber)}${primeCount > 0 ? "'".repeat(primeCount) : ''}`;
}

export interface SypStaff {
  voice?: Voice;
  clef: SypClef;
  notes: Notes;
}

export type StaffIndex = number;

export function isVocalVoice(voice: Voice): boolean {
  return voice === Voice.Soprano || voice === Voice.Alto || voice === Voice.Tenor || voice === Voice.Bass;
}

export interface SypUnisonStaff extends SypStaff {
  notes: SypNote[];
}

export function isTrebleClef({ clef }: SypStaff) {
  return clef === SypClef.G;
}

export function isBassClef({ clef }: SypStaff) {
  return clef === SypClef.F;
}

export function areStavesEqual(lhs: SypStaff, rhs: SypStaff) {
  return lhs.voice === rhs.voice && lhs.clef === rhs.clef;
}

export function areSameClefAndVoice(lhs: SypStaff, rhs: SypStaff) {
  return lhs.clef === rhs.clef && lhs.voice === rhs.voice;
}

export function isNewMeasure(measure: SypMeasure | SypMeasureNew): measure is SypMeasureNew {
  return Boolean((measure as any).staves) && (measure as any).staves.length > 0;
}

export function getTimeSignature(measure: SypMeasure): SypTimeSignature | undefined {
  return measure.rhythm === undefined ? undefined : getTimeSignatureFromRhythm(measure.rhythm, measure.number);
}

export function getTimeSignatureFromRhythm(rhythm: SypRhythm, measureNumber?: number): SypTimeSignature {
  const match = rhythm.match(/(?<beats>(\d+|O))\s*:\s*(?<beatType>\d+)/);
  if (!match || !match.groups) {
    throw new Error(`Malformed rhythm "${rhythm}" in measure ${measureNumber}`);
  }
  // the letter 'O' appears in some songs in place of 0
  return {
    beats: match.groups.beats === 'O' ? 0 : Number(match.groups.beats),
    beatType: Number(match.groups.beatType),
  };
}

export function getBeatCountForTimeSignature(timeSignature: SypTimeSignature) {
  return timeSignature.beats * (4 / timeSignature.beatType);
}

export function canCombineMeasures(lhs: SypMeasureNew, rhs: SypMeasureNew) {
  return (
    lhs.key === rhs.key &&
    lhs.mode === rhs.mode &&
    lhs.rhythm === rhs.rhythm &&
    lhs.tempo === rhs.tempo &&
    lhs.verseCount === rhs.verseCount &&
    lhs.barline !== SypBarlineType.Double &&
    lhs.goto === undefined &&
    lhs.setIteration === undefined &&
    rhs.setIteration === undefined &&
    !lhs.beginRepeat &&
    !rhs.beginRepeat &&
    !lhs.endRepeat &&
    !rhs.endRepeat &&
    JSON.stringify(lhs.iterations) === JSON.stringify(rhs.iterations)
  );
}

export function combineMeasures(lhs: SypMeasureNew, rhs: SypMeasureNew) {
  if (!canCombineMeasures(lhs, rhs)) {
    throw new Error(`Cannot combine measures ${JSON.stringify(lhs)} ${JSON.stringify(rhs)}`);
  }
  const combinedMeasure: SypMeasureNew = { ...rhs, ...lhs };
  combinedMeasure.staves = combineStaves(lhs.staves, rhs.staves);
  if (rhs.barline) {
    combinedMeasure.barline = rhs.barline;
  } else {
    delete combinedMeasure.barline;
  }
  return combinedMeasure;
}

export function canCombineStaves(lhs: SypStaff[], rhs: SypStaff[]): boolean {
  for (const lhsStaff of lhs) {
    if (!rhs.find((rhsStaff) => areSameClefAndVoice(lhsStaff, rhsStaff))) {
      return false;
    }
  }
  for (const rhsStaff of rhs) {
    if (!lhs.find((lhsStaff) => areSameClefAndVoice(rhsStaff, lhsStaff))) {
      return false;
    }
  }
  return true;
}

export function combineStaves(lhs: SypStaff[], rhs: SypStaff[]): SypStaff[] {
  if (!canCombineStaves(lhs, rhs)) {
    throw new Error(`staves cannot be combined: ${JSON.stringify(lhs)} ${JSON.stringify(rhs)}`);
  }
  const combinedStaves: SypStaff[] = [];
  for (const lhsStaff of lhs) {
    const rhsNotes = rhs.find((rhsStaff) => areSameClefAndVoice(lhsStaff, rhsStaff))?.notes ?? [];
    const combinedStaff: SypStaff = {
      ...lhsStaff,
      notes: [...lhsStaff.notes, ...rhsNotes],
    };
    combinedStaves.push(combinedStaff);
  }
  return combinedStaves;
}

export enum SypStep {
  A = 'a',
  B = 'b',
  C = 'c',
  D = 'd',
  E = 'e',
  F = 'f',
  G = 'g',
}

export enum OctaveShift {
  DoubleUp = '++',
  Up = '+',
  Down = '-',
  DoubleDown = '--',
}

export enum SypAccidental {
  Sharp = '#',
  Flat = 'b',
  Natural = 'n',
  DoubleSharp = '##',
  DoubleFlat = 'bb',
}

export enum SypNotation {
  TieWithNextNote = 'TieWithNextNote',
  SlurWithNextNote = 'SlurWithNextNote',
}

export enum SypBarlineType {
  Single = 'single',
  Double = 'double',
}

export const sypBarlineRepresentation: { [symbol: string]: SypBarlineType } = {
  ['|']: SypBarlineType.Single,
  ['||']: SypBarlineType.Double,
};

export type Notes = Array<SypNote | SypChord>;

export function isWholeNoteRest(notes: Notes) {
  return (
    notes.length === 1 && !isSypChord(notes[0]) && notes[0].isRest && notes[0].denominator === 1 && !notes[0].dotCount
  );
}

export interface SypNoteData {
  step?: SypStep;
  denominator: number;
  accidental?: SypAccidental;
  octaveShift?: OctaveShift; // TODO(hewitt): eliminate (syp representation only -> new format uses 'octave')
  dotCount?: number;
  fermata?: number;
  notation?: SypNotation;
  octave?: number;
  clef?: SypClef;
  isRest?: boolean;
  stem?: SypStem;
  hasLyrics?: boolean;
}

export class SypNote implements SypNoteData {
  step?: SypStep;
  denominator: number;
  accidental?: SypAccidental;
  octaveShift?: OctaveShift; // TODO(hewitt): eliminate (syp representation only -> new format uses 'octave')
  dotCount?: number;
  fermata?: number;
  notation?: SypNotation;
  octave?: number;
  clef?: SypClef;
  isRest?: boolean;
  stem?: SypStem;
  hasLyrics?: boolean;

  // NOTE: used as a copy constructor, so must not have any data members outside these
  constructor({
    step,
    denominator,
    accidental,
    octaveShift,
    dotCount,
    fermata,
    notation,
    octave,
    clef,
    isRest,
    stem,
    hasLyrics,
  }: SypNoteData) {
    if (step !== undefined) {
      this.step = step;
    } else {
      delete this.step;
    }
    this.denominator = denominator;
    if (accidental !== undefined) {
      this.accidental = accidental;
    } else {
      delete this.accidental;
    }
    if (octaveShift !== undefined) {
      this.octaveShift = octaveShift;
    } else {
      delete this.octaveShift;
    }
    if (dotCount !== undefined) {
      this.dotCount = dotCount;
    } else {
      delete this.dotCount;
    }
    if (fermata !== undefined) {
      this.fermata = fermata;
    } else {
      delete this.fermata;
    }
    if (notation !== undefined) {
      this.notation = notation;
    } else {
      delete this.notation;
    }
    if (octave !== undefined) {
      this.octave = octave;
    } else {
      delete this.octave;
    }
    this.clef = clef;
    if (isRest) {
      this.isRest = isRest;
    } else {
      delete this.isRest;
    }
    if (stem) {
      this.stem = stem;
    } else {
      delete this.stem;
    }
    if (hasLyrics) {
      this.hasLyrics = hasLyrics;
    } else {
      delete this.hasLyrics;
    }

    if (!this.isRest && !this.step) {
      throw new Error(`Must specify either rest or step for note ${this.toString()}`);
    }

    if (this.step && !Object.values(SypStep).includes(this.step)) {
      throw new Error(`Unknown SypStep for note "${this.step}"`);
    }

    if (this.clef) {
      // new file format
      if (this.octaveShift) {
        throw new Error(`octaveShift not supported in new file format ${this.toString()}`);
      }
      if (this.octave === undefined && !this.isRest) {
        throw new Error(`expected octave for note in new file format ${this.toString()}`);
      }
    } else {
      // old file format
      if (this.octave !== undefined) {
        throw new Error(`unexpected octave for note in old file format: ${this.toString()}`);
      }
    }
  }

  get beatCount(): number {
    // avoids decimal arithmetic
    const { numeratorMultiplier, denominatorMultiplier } = this.beatMultiplier;
    return (4 * numeratorMultiplier) / (this.denominator * denominatorMultiplier);
  }

  private get beatMultiplier(): { numeratorMultiplier: number; denominatorMultiplier: number } {
    switch (this.dotCount) {
      case undefined:
      case 0:
        return { numeratorMultiplier: 1, denominatorMultiplier: 1 };
      case 1:
        return { numeratorMultiplier: 3, denominatorMultiplier: 2 };
      case 2:
        return { numeratorMultiplier: 7, denominatorMultiplier: 4 };
      default:
        throw new Error(`Unsupported dot count ${this.dotCount}`);
    }
  }

  private getOctaveShift(): OctaveShift | undefined {
    if (this.clef) {
      // new file format
      if (this.octaveShift) {
        throw new Error(`unexpected note octave shift in new file format ${this.toString()}`);
      }
      const { octave } = this;
      if (octave === undefined || !this.step) {
        return;
      }
      const defaultOctave = getDefaultOctaveForClefAndStep(this.clef, this.step);
      if (octave < defaultOctave) {
        return defaultOctave - octave === 1 ? OctaveShift.Down : OctaveShift.DoubleDown;
      } else if (octave > defaultOctave) {
        return octave - defaultOctave === 1 ? OctaveShift.Up : OctaveShift.DoubleUp;
      }
    } else {
      // old file format
      return this.octaveShift;
    }
  }

  get stemChar() {
    const { stem } = this;
    if (!stem) {
      return;
    }
    switch (stem) {
      case SypStem.Up:
        return StemUpChar;
      case SypStem.Down:
        return StemDownChar;
      case SypStem.Double:
        return StemDoubleChar;
      default:
        ensureUnreachable(stem);
    }
  }

  toString() {
    return (
      (this.isRest ? 'R' : '') +
      (this.step ?? '') +
      (this.accidental ?? '') +
      (this.getOctaveShift() ?? '') +
      (this.denominator === 4 ? '' : '/' + this.denominator.toString()) +
      (this.dotCount ? '.'.repeat(this.dotCount) : '') +
      (this.stemChar ?? '') +
      (this.fermata ? '(' + this.fermata.toString() + ')' : '') +
      (this.notation === SypNotation.TieWithNextNote ? ' =' : '') +
      (this.notation === SypNotation.SlurWithNextNote ? ' -' : '')
    );
  }
}

export class SypChord {
  constructor(public notes: SypNote[]) {}
  toString() {
    return `(${this.notes.map((note) => note.toString()).join(' ')})`;
  }
  get hasLyrics() {
    return this.notes.some((note) => note.hasLyrics);
  }
}

export function isSypChord(noteOrChord: SypNote | SypChord): noteOrChord is SypChord {
  return !!(noteOrChord as any).notes;
}

// contains at least one chord with notes of differing durations - requires part splitting to handle properly
export function containsHeterogeneousChords(notes: Notes): boolean {
  return notes.some((note) => isSypChord(note) && isHeterogeneousChord(note));
}

export function isHeterogeneousChord(chord: SypChord): boolean {
  const uniqueNoteBeatCounts = new Set(chord.notes.map((note) => getBeatCountForSypNote(note)));
  return uniqueNoteBeatCounts.size > 1;
}

export function getNotesFromNoteOrChord(maybeChord: SypNote | SypChord): SypNote[] {
  return isSypChord(maybeChord) ? maybeChord.notes : [maybeChord];
}

export function parseSypFileNew(content: string, filename?: string): SypMusicFileNew {
  return parseSypFile(content, { filename, newFormat: true });
}

export function allNotesHaveSameDuration(notes: SypNote[]): boolean {
  return new Set(notes.map((note) => getBeatCountForSypNote(note))).size === 1;
}

export function parseSypFile(
  content: string,
  { filename, newFormat }: { filename?: string; newFormat?: boolean } = {},
): SypMusicFileNew {
  const metadata: Partial<SypSongMetadata> = {};
  const measures: SypMeasureNew[] = [];
  const messages: SypFileMessage[] = [];
  let rhythm = defaultRhythm;
  let measureKey = defaultSongKey;
  let mode = defaultMode;
  let tempo = getDefaultTempo(rhythm);
  let isUnison: boolean | undefined = undefined;
  let rythmBeatCount = getBeatCountForTimeSignature(getTimeSignatureFromRhythm(rhythm, 0));

  function combineTiesAndSlursWithPriorNotes(noteStrings: string[]) {
    const combinedNoteStrings: string[] = [];
    for (let index = 0; index < noteStrings.length; index++) {
      if (index === noteStrings.length - 1) {
        combinedNoteStrings.push(noteStrings[index]);
        break;
      }
      if (noteStrings[index + 1] === '=' || noteStrings[index + 1] === '-') {
        combinedNoteStrings.push(noteStrings[index] + ' ' + noteStrings[index + 1]);
        index += 1;
      } else {
        combinedNoteStrings.push(noteStrings[index]);
      }
    }
    return combinedNoteStrings;
  }

  for (const line of content.split(/\r?\n/)) {
    if (line.trim().length === 0) {
      continue;
    }
    const match = line.match(/(?<key>\S+)\s*(?<value>.*)/);
    if (!match || !match.groups) {
      throw new Error(`No key value pair found for line: ${line} of file ${filename}`);
    }
    const { key, value } = match.groups as { key: SypMusicFileKey | Voice; value: string };
    if (Object.values(Voice).includes(key as Voice) || key === SypMusicFileKey.Unison) {
      if (newFormat && key === SypMusicFileKey.Unison) {
        metadata[key] = value;
        continue;
      }

      if (measures.length === 0) {
        if (newFormat) {
          throw new Error(`Expected 'measure' directive prior to voices in syp file format`);
        } else {
          // Hack so that browser can load .txt files
          // this will fail to catch missing measure directives in the old format
          // this hack is necessary because we are not properly updating the cc2020 offline hymnal files due to
          // incorrect manifest song number keys ("01" instead of "001"), so people have the old version
          // of the songs (e.g., cc2020 466) without measure directives.  The browser doesn't use
          // the vocal notes anyway, so only need to prevent it from crashing here.
          const measure: SypMeasureNew = {
            number: 1,
            soprano: [],
            alto: [],
            tenor: [],
            bass: [],
            staves: [],
            tempo: getDefaultTempo(),
            rhythm: defaultRhythm,
            key: defaultSongKey,
            mode: defaultMode,
            beatCount: rythmBeatCount,
          };
          measures.push(measure);
        }
      }
      const rawStrings = detachChordTokens(
        value
          .split(/(\s+)/)
          .map((element) => element.trim())
          .filter((element) => element.length > 0),
      );
      const combinedStrings = combineTiesAndSlursWithPriorNotes(rawStrings);
      const { clef, noteTokens, barline } = splitClefFromNoteTokens(combinedStrings);
      if (newFormat) {
        const measure = measures[measures.length - 1];
        const voice = key as Voice;
        const notes: Notes = [];
        let currentChord: SypChord | undefined;
        for (const token of noteTokens) {
          if (token === '(') {
            if (currentChord) {
              throw new Error(`Nested chords not supported ${filename}`);
            }
            currentChord = new SypChord([]);
          } else if (token === ')') {
            if (!currentChord) {
              throw new Error(`Close parenthesis before open parenthesis in ${filename}`);
            }
            notes.push(currentChord);
            currentChord = undefined;
          } else {
            const note = getSypNoteFromString(token, {
              clef:
                clef ??
                (key === SypMusicFileKey.Unison || voicesRequiringClef.includes(voice)
                  ? SypClef.G
                  : getSypClefFromVoice(voice)),
              // No need to worry about the "vocal" voice in legacy syp files
              ...(!!metadata[SypMusicFileKey.Voice] && {
                voice: key === SypMusicFileKey.Unison ? Voice.Soprano : (voice as Voice),
              }),
            });
            if (currentChord) {
              currentChord.notes.push(note);
            } else {
              notes.push(note);
            }
          }
        }
        if (currentChord) {
          throw new Error(`Missing close parenthesis for chord in ${filename}`);
        }
        measure.barline = barline ?? measure.barline;
        measure.staves = [
          ...(measure.staves ?? []),
          {
            voice,
            clef: clef ?? (key === SypMusicFileKey.Unison ? SypClef.G : getSypClefFromVoice(voice)),
            notes,
          },
        ];
      } else {
        const notes = noteTokens.map((element) => getSypNoteFromString(element));
        if (key === SypMusicFileKey.Unison) {
          isUnison = true;
        }
        if (![Voice.Soprano, Voice.Alto, Voice.Tenor, Voice.Bass, SypMusicFileKey.Unison].includes(key)) {
          throw new Error(`${key} voice not supported in old music file format`);
        }
        const voice =
          key === SypMusicFileKey.Unison
            ? Voice.Soprano
            : (key as Voice.Soprano | Voice.Alto | Voice.Tenor | Voice.Bass);
        // map unison to soprano
        if (key === SypMusicFileKey.Unison) {
          delete measures[measures.length - 1].alto;
          delete measures[measures.length - 1].tenor;
          delete measures[measures.length - 1].bass;
        } else if (isUnison) {
          throw new Error(`Non-unison part ${key} mixed with unison part`);
        }
        ensureExists(measures[measures.length - 1][voice]).push(...notes);
      }
    } else {
      switch (key) {
        case SypMusicFileKey.Measure:
          measures.push(
            newFormat
              ? {
                  number: Number(value),
                  key: measureKey,
                  mode,
                  rhythm,
                  tempo,
                  soprano: [], // TODO(hewitt): eliminate 'soprano'
                  staves: [],
                  beatCount: rythmBeatCount,
                }
              : {
                  number: Number(value),
                  key: measureKey,
                  mode,
                  rhythm,
                  tempo,
                  soprano: [],
                  alto: [],
                  tenor: [],
                  bass: [],
                  staves: [],
                  beatCount: rythmBeatCount,
                },
          );
          break;
        case SypMusicFileKey.Rhythm:
          rhythm = value;
          rythmBeatCount = getBeatCountForTimeSignature(
            getTimeSignatureFromRhythm(rhythm, measures[measures.length - 1]?.number ?? 0),
          );
          if (!metadata[key]) {
            metadata[key] = value;
          }
          if (measures.length > 0) {
            measures[measures.length - 1].rhythm = value;
            measures[measures.length - 1].beatCount = rythmBeatCount;
          }
          break;
        case SypMusicFileKey.Key:
          measureKey = value;
          if (!metadata[key]) {
            metadata[key] = value;
          }
          if (measures.length > 0) {
            measures[measures.length - 1].key = value;
          }
          break;
        case SypMusicFileKey.BeatCount:
          if (measures.length > 0) {
            measures[measures.length - 1].beatCount = Number(value);
          }
          break;
        case SypMusicFileKey.Iterations: {
          const { iterations } = value.trim().match(/^(?<iterations>\[(\d+,?)+\])$/)?.groups ?? {};
          if (!iterations) {
            console.error(`Malformed iterations payload: ${value}`);
          } else {
            measures[measures.length - 1].iterations = JSON.parse(iterations);
          }
          break;
        }
        case SypMusicFileKey.Goto: {
          const { toMeasure, forIterations } =
            value.trim().match(/^(?<toMeasure>\d+(\.\d+)?)(?<forIterations>\s+\[(\d+,?)+\])?$/)?.groups ?? {};
          if (!toMeasure) {
            console.error(`Malformed goto payload: ${value}`);
          } else {
            measures[measures.length - 1].goto = {
              toMeasure: Number(toMeasure),
              ...(forIterations && { forIterations: JSON.parse(forIterations) }),
            };
          }
          break;
        }
        case SypMusicFileKey.End: {
          const { forIterations } = value.trim().match(/^(?<forIterations>\[(\d+,?)+\])?$/)?.groups ?? {};
          measures[measures.length - 1].end = { ...(forIterations && { forIterations: JSON.parse(forIterations) }) };
          break;
        }
        case SypMusicFileKey.SetIteration: {
          const { toMeasure } = value.trim().match(/^(?<toMeasure>\d+(\.\d)?)$/)?.groups ?? {};
          if (!toMeasure) {
            console.error(`Malformed set_iteration payload: ${value}`);
          } else {
            measures[measures.length - 1].setIteration = Number(toMeasure);
          }
          break;
        }
        case SypMusicFileKey.BeginRepeat:
          measures[measures.length - 1].beginRepeat = true;
          break;
        case SypMusicFileKey.EndRepeat:
          measures[measures.length - 1].endRepeat = true;
          break;
        case SypMusicFileKey.Info:
        case SypMusicFileKey.Warning:
        case SypMusicFileKey.Error:
          messages.push({ type: getSypFileMessageType(key), text: value });
          break;
        default:
          if (key === SypMusicFileKey.Mode) {
            mode = value as SypMode;
            if (measures.length > 0) {
              measures[measures.length - 1].mode = mode;
            }
          }
          if (key === SypMusicFileKey.Tempo) {
            tempo = Number(value);
            if (measures.length > 0) {
              measures[measures.length - 1].tempo = tempo;
            }
          }
          metadata[key] = value;
          break;
      }
    }
  }

  if (!useServerCalculatedBeatCount) {
    for (const measure of measures) {
      measure.beatCount = getMeasureBeatCount(measure, { includeFermatas: true });
    }
  }

  return {
    ...(isUnison && { isUnison }),
    metadata: getFullMetadataFromPartialMetadata(metadata, measures[0], filename),
    measures,
    messages,
  };
}

export function getFullMetadataFromPartialMetadata(
  metadata: Partial<SypSongMetadata>,
  firstMeasure: SypMeasureNew | undefined,
  filename: string | undefined,
): SypSongMetadata {
  return {
    [SypMusicFileKey.Rhythm]: firstMeasure?.rhythm ?? defaultRhythm,
    [SypMusicFileKey.Tempo]: (firstMeasure?.tempo ?? getDefaultTempo(firstMeasure?.rhythm)).toString(),
    [SypMusicFileKey.Mode]: firstMeasure?.mode ?? defaultMode,
    [SypMusicFileKey.Key]: firstMeasure?.key ?? defaultSongKey,
    [SypMusicFileKey.Title]: getTitleFromFilename(filename),
    ...metadata,
  };
}

function getTitleFromFilename(filename?: string): string {
  if (!filename) {
    return 'Untitled';
  }
  // Note: Cannot use Node's path module b/c must run on client as well
  const basename = filename.substring(filename.lastIndexOf('/') + 1);
  const filenameWithoutExtension =
    basename[basename.length - 4] === '.' ? basename.substring(0, basename.length - 4) : basename;
  const { title } = filenameWithoutExtension.match(/^(\S+ - )?(?<title>.*)$/)?.groups ?? {};
  return title ?? filenameWithoutExtension;
}

export function getSypNoteFromString(text: string, { clef, voice }: { clef?: SypClef; voice?: Voice } = {}): SypNote {
  const match = text.match(
    /^(?<rest>[rR])?(?<step>[a-gA-G])?(?<accidental>(#|n|b|##|bb))?(?<octave_shift>(\+|-|\+\+|--))?(\/(?<denominator>\d+(\.\d+)?))?(?<dotCount>\.+)?(?<stem>[\u2191\u2193\u2195])?(\((?<fermata>[0-9]+(\.[0-9]+)?)\))?((?<tie>\s+=)|(?<slur>\s+-))?$/,
  );
  if (!match || !match.groups) {
    throw new Error(`Unexpected note value "${text}" in Sing Your Part text file`);
  }
  const { rest, step, accidental, octave_shift, denominator, dotCount, fermata, tie, stem, slur } = match.groups;
  const sypStep = step ? (step.toLowerCase() as SypStep) : undefined;
  if (sypStep && !Object.values(SypStep).includes(sypStep)) {
    throw new Error(`Unexpected step "${sypStep}" value in Sing Your Part text file`);
  }
  let octave: number | undefined;
  if (clef && sypStep) {
    const defaultOctave = voice
      ? getDefaultOctaveForVoiceAndStep(voice, sypStep)
      : getDefaultOctaveForClefAndStep(clef, sypStep);
    const octaveShift = octave_shift as OctaveShift | undefined;
    switch (octaveShift) {
      case OctaveShift.DoubleUp:
        octave = defaultOctave + 2;
        break;
      case OctaveShift.Up:
        octave = defaultOctave + 1;
        break;
      case undefined:
        octave = defaultOctave;
        break;
      case OctaveShift.Down:
        octave = defaultOctave - 1;
        break;
      case OctaveShift.DoubleDown:
        octave = defaultOctave - 2;
        break;
      default:
        ensureUnreachable(octaveShift);
    }
  }
  // use VOICE here
  return new SypNote({
    step: sypStep,
    denominator: denominator === undefined ? 4 : Number(denominator),
    ...(octave !== undefined && { octave }),
    ...(accidental && { accidental: accidental as SypAccidental }),
    // TODO(hewitt): Eliminate octave_shift (text representation only) -> calculate octave based on clef
    ...(!clef && octave_shift && { octaveShift: octave_shift as OctaveShift }),
    ...(dotCount && dotCount.length > 0 && { dotCount: dotCount.length }),
    ...(fermata && { fermata: Number(fermata) }),
    ...(tie && { notation: SypNotation.TieWithNextNote }),
    ...(!tie && slur && { notation: SypNotation.SlurWithNextNote }),
    ...(clef && { clef }),
    ...(rest && { isRest: true }),
    ...(stem && {
      stem: stem === StemUpChar ? SypStem.Up : stem === StemDownChar ? SypStem.Down : SypStem.Double,
    }),
  });
}

// A0 = 1, C8 = 88
export function getPianoIndex(step: string, octave: number, accidental?: SypAccidental): number;
export function getPianoIndex(step: undefined, octave: number, accidental?: SypAccidental): undefined;
export function getPianoIndex(step: string, octave: undefined, accidental?: SypAccidental): undefined;
export function getPianoIndex(
  step: string | undefined,
  octave: number | undefined,
  accidental?: SypAccidental,
): number | undefined;
export function getPianoIndex(
  step: string | undefined,
  octave: number | undefined,
  accidental?: SypAccidental,
): number | undefined {
  const letterValue: { [key: string]: number } = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  step = step?.toUpperCase();
  if (step === undefined || octave === undefined) {
    return undefined;
  }
  if (!(step in letterValue)) {
    throw new Error(`Unrecognized note step value ${step}`);
  }
  return 12 * octave + letterValue[step] + getAccidentalValue(accidental) - 8;
}

// The following default ranges are assumed by SYP voices:
// soprano E4 to D5
// alto    A3 to G4
// tenor   E3 to D4
// bass    A2 to G3
// Going above / below each default range requires an octave shift ('+' / '-')
export function getOctaveShift(
  voice: Voice,
  step: string | undefined,
  octave: number | undefined,
): OctaveShift | undefined {
  if (!step || octave === undefined) {
    throw new Error(`Unable to calculate octave shift without step`);
  }
  const pianoIndex = getPianoIndex(step, octave);
  if (!pianoIndex) {
    return undefined;
  }

  switch (voice) {
    case Voice.Soprano:
      if (pianoIndex < getPianoIndex('E', 4)) {
        return OctaveShift.Down;
      } else if (pianoIndex > getPianoIndex('D', 5)) {
        return OctaveShift.Up;
      } else {
        return undefined;
      }
    case Voice.Alto:
      if (pianoIndex < getPianoIndex('A', 3)) {
        return OctaveShift.Down;
      } else if (pianoIndex > getPianoIndex('G', 4)) {
        return OctaveShift.Up;
      } else {
        return undefined;
      }
    case Voice.Tenor:
      if (pianoIndex < getPianoIndex('E', 3)) {
        return OctaveShift.Down;
      } else if (pianoIndex > getPianoIndex('D', 4)) {
        return OctaveShift.Up;
      } else {
        return undefined;
      }
    case Voice.Bass:
      if (pianoIndex < getPianoIndex('A', 2)) {
        return OctaveShift.Down;
      } else if (pianoIndex > getPianoIndex('G', 3)) {
        return OctaveShift.Up;
      } else {
        return undefined;
      }
    default:
      throw new Error(`Unrecognized voice "${voice}" (expecting soprano, alto, tenor or bass)`);
  }
}

export function getAccidentalValue(accidental?: SypAccidental): number {
  switch (accidental) {
    case SypAccidental.DoubleFlat:
      return -2;
    case SypAccidental.Flat:
      return -1;
    case SypAccidental.Natural:
    case undefined:
      return 0;
    case SypAccidental.Sharp:
      return 1;
    case SypAccidental.DoubleSharp:
      return 2;
    default:
      ensureUnreachable(accidental);
  }
}

export function getBeatCountForSypNote(note: SypNote, { includeFermatas }: { includeFermatas?: boolean } = {}) {
  const baseCount = 4 / note.denominator;
  const fermataBeats = (includeFermatas && note.fermata) || 0;
  if (note.dotCount === undefined) {
    return baseCount + fermataBeats;
  }
  if (note.dotCount === 1) {
    return (baseCount * 3) / 2 + fermataBeats;
  }
  if (note.dotCount === 2) {
    return (baseCount * 7) / 4 + fermataBeats;
  }
  throw new Error(`Unsupported dot count ${note.dotCount}`);
}

export function getBeatCountForSypNotes(notes: SypNote[], { includeFermatas }: { includeFermatas?: boolean } = {}) {
  return notes.reduce((acc, note) => acc + getBeatCountForSypNote(note, { includeFermatas }), 0);
}

export function getMinBeatCountForSypChord(chord: SypChord, { includeFermatas }: { includeFermatas?: boolean } = {}) {
  if (chord.notes.length === 0) {
    return 0;
  }
  const beatCounts = chord.notes.map((note) => getBeatCountForSypNote(note, { includeFermatas }));
  return Math.min(...beatCounts);
}

export function getMaxBeatCountForSypChord(chord: SypChord, { includeFermatas }: { includeFermatas?: boolean } = {}) {
  if (chord.notes.length === 0) {
    return 0;
  }
  const beatCounts = chord.notes.map((note) => getBeatCountForSypNote(note, { includeFermatas }));
  return Math.max(...beatCounts);
}

function splitClefFromNoteTokens(rawStrings: string[]): {
  clef?: SypClef;
  noteTokens: string[];
  barline?: SypBarlineType;
} {
  if (rawStrings.length === 0) {
    return { noteTokens: [] };
  }
  const clefName = rawStrings[0].match(/^(?<clefName>[GF]):/)?.groups?.clefName;
  if (clefName && clefName !== 'G' && clefName !== 'F') {
    throw new Error(`Unrecognized clef name ${clefName}`);
  }
  const clef = clefName ? sypClefName[clefName] : undefined;
  const barline = sypBarlineRepresentation[rawStrings[rawStrings.length - 1]];
  const noteTokens = rawStrings.slice(clef ? 1 : 0, rawStrings.length - (barline ? 1 : 0));
  return { clef, noteTokens, barline };
}

function detachChordTokens(rawStrings: string[]): string[] {
  const tokens: string[] = [];
  for (const rawString of rawStrings) {
    let match = rawString.match(/^(?<openParen>\()?(?<noteString>[^()]+)?(?<closeParen>\))?(\s+[=-])?$/);
    if (!match) {
      // might be a fermata (e.g. 'f(1.4)' with optional parenthesis (e.g. '(f(1.4)' or 'f(1.4))' )
      match = rawString.match(/^(?<openParen>\()?(?<noteString>[^()]+\([^()]+\))?(?<closeParen>\))?(\s+[=-])?$/);
    }
    if (!match) {
      throw new Error(`Unrecognized token ${rawString}`);
    }
    const { openParen, noteString, closeParen } = match.groups ?? {};
    if (openParen) {
      tokens.push('(');
    }
    if (noteString) {
      tokens.push(noteString);
    }
    if (closeParen) {
      tokens.push(')');
    }
  }
  return tokens;
}

export function getSypClefFromVoice(voice: Voice): SypClef {
  switch (voice) {
    case Voice.Soprano:
    case Voice.Soprano1:
    case Voice.Soprano2:
    case Voice.SopranoSolo:
    case Voice.Alto:
    case Voice.Alto1:
    case Voice.Alto2:
    case Voice.AltoSolo:
    case Voice.MezzoSoprano:
    case Voice.CounterTenor:
    case Voice.Contralto:
    case Voice.Descant:
    case Voice.Treble:
    case Voice.Group1:
    case Voice.Group2:
    case Voice.Group3:
    case Voice.Group4:
    case Voice.Flute:
    case Voice.Oboe:
    case Voice.Violin:
    case Voice.Trumpet:
      return SypClef.G;
    case Voice.Cello:
    case Voice.Tenor:
    case Voice.Tenor1:
    case Voice.Tenor2:
    case Voice.TenorSolo:
    case Voice.Bass:
    case Voice.Bass1:
    case Voice.Bass2:
    case Voice.BassSolo:
    case Voice.Baritone:
    case Voice.Cantor:
      return SypClef.F;
    case Voice.Melody:
    case Voice.Melody1:
    case Voice.Melody2:
    case Voice.Piano:
    case Voice.Vocal:
      // case Voice.Unison:
      throw new Error(`Clef must be specified for ${voice} staff`);
    default:
      ensureUnreachable(voice);
  }
}

function getDefaultOctaveForClefAndStep(clef: SypClef, step: SypStep): number {
  if (clef === SypClef.G) {
    switch (step) {
      case SypStep.F:
      case SypStep.G:
      case SypStep.A:
      case SypStep.B:
        return 4;
      case SypStep.C:
      case SypStep.D:
      case SypStep.E:
        return 5;
      default:
        ensureUnreachable(step);
    }
  } else if (clef === SypClef.F) {
    switch (step) {
      case SypStep.A:
      case SypStep.B:
        return 2;
      case SypStep.C:
      case SypStep.D:
      case SypStep.E:
      case SypStep.F:
      case SypStep.G:
        return 3;
      default:
        ensureUnreachable(step);
    }
  } else {
    ensureUnreachable(clef);
  }
}

// backward compatibility with legacy file format
// voice      soprano E4 to D5
// voice      alto    A3 to G4
// voice      tenor   E3 to D4
// voice      bass    A2 to G3
export function getDefaultOctaveForVoiceAndStep(voice: Voice, step: SypStep): number {
  const noteRanges: {
    [voiceOrClef in Voice | SypClef]?: {
      from: Required<Pick<SypNote, 'step' | 'octave'>>;
      to: Required<Pick<SypNote, 'step' | 'octave'>>;
    };
  } = {
    [Voice.Soprano]: { from: { step: SypStep.E, octave: 4 }, to: { step: SypStep.D, octave: 5 } },
    [Voice.Alto]: { from: { step: SypStep.A, octave: 3 }, to: { step: SypStep.G, octave: 4 } },
    [Voice.Tenor]: { from: { step: SypStep.E, octave: 3 }, to: { step: SypStep.D, octave: 4 } },
    [Voice.Bass]: { from: { step: SypStep.A, octave: 2 }, to: { step: SypStep.G, octave: 3 } },
  };
  const noteRange = noteRanges[voice];
  if (!noteRange) {
    throw new Error(`Missing note range for voice ${voice}`);
  }
  return step >= SypStep.C && step <= noteRange.to.step ? noteRange.to.octave : noteRange.from.octave;
}

export interface SypFileMessage {
  type: MessageType;
  measure?: MeasureNumber;
  text: string;
}

export function getSypFileMessageType(key: SypMusicFileKey): MessageType {
  switch (key) {
    case SypMusicFileKey.Info:
      return MessageType.Info;
    case SypMusicFileKey.Warning:
      return MessageType.Warning;
    case SypMusicFileKey.Error:
      return MessageType.Error;
    default:
      throw new Error(`Expected key ${key} to be a message type`);
  }
}

export interface MeasureVisit {
  measure: SypMeasureNew;
  iteration: number;
}

// visit all measures from beginning to end of the song, respecting `iterations` & `goto` directives
export function visitAllMeasures(measures: SypMeasureNew[]): MeasureVisit[] {
  const visits: MeasureVisit[] = [];
  const iterationStack: number[] = [1];
  const scopeOrigins: MeasureNumber[] = [-1]; // measure number where each scope was created
  let measureNumber: MeasureNumber | undefined = measures[0]?.number;
  let arrivedViaGoto = false;
  const nextMeasureNumber = new Map<MeasureNumber, MeasureNumber>();
  for (let i = 0; i < measures.length - 1; i++) {
    nextMeasureNumber.set(measures[i].number, measures[i + 1].number);
  }
  const measuresByNumber = new Map(measures.map((measure) => [measure.number, measure]));
  while (measureNumber !== undefined) {
    const measure = measuresByNumber.get(measureNumber);
    if (!measure) {
      throw new Error(`Measure should have been added to map ${measureNumber}`);
    }
    if (visits.length > 10000) {
      throw new Error(`Infinite goto loop near measure ${measure.number}`);
    }
    // beginRepeat pushes a new scope (only on sequential arrival, not when arriving via goto)
    if (measure.beginRepeat && !arrivedViaGoto) {
      iterationStack.push(1);
      scopeOrigins.push(measure.number);
    }
    arrivedViaGoto = false;
    const iteration = iterationStack[iterationStack.length - 1];
    if (!measure.iterations || measure.iterations.includes(iteration)) {
      visits.push({ measure, iteration });
    }
    if (measure.end && (!measure.end.forIterations || measure.end.forIterations.includes(iteration))) {
      break;
    }
    if (measure.goto && (!measure.goto.forIterations || measure.goto.forIterations.includes(iteration))) {
      // pop any inner scopes that the goto is jumping out of
      while (iterationStack.length > 1 && scopeOrigins[scopeOrigins.length - 1] > measure.goto.toMeasure) {
        iterationStack.pop();
        scopeOrigins.pop();
      }
      measureNumber = measure.goto.toMeasure;
      iterationStack[iterationStack.length - 1] += 1;
      arrivedViaGoto = true;
    } else {
      // endRepeat: pop the scope when no goto fired
      if (measure.endRepeat && iterationStack.length > 1) {
        iterationStack.pop();
        scopeOrigins.pop();
      }
      // legacy setIteration: push new scope after visiting the measure
      if (measure.setIteration) {
        iterationStack.push(1);
        scopeOrigins.push(measure.number);
      }
      measureNumber = nextMeasureNumber.get(measureNumber);
    }
  }
  return visits;
}
